import {userStore} from "@/Stores/UserStore";
import axios from "axios";
import {fetchAuthSession} from "aws-amplify/auth";
import {useAuthenticator} from "@aws-amplify/ui-vue";
import { nextTick } from 'vue'

export class ApiWrapper {

    static localPath = import.meta.env.VUE_APP_PATH;

    static async getSession(refresh = false) {
        const { tokens } = await fetchAuthSession({forceRefresh: refresh});
        if(tokens) {
            const idToken = tokens?.idToken;
            userStore.setIdToken(idToken);
            const accessToken = tokens?.accessToken;
            userStore.setAccessToken(accessToken);

            const userInfoStr = tokens.idToken?.payload?.userInfo;
            if(userInfoStr) {
                const userInfo = JSON.parse(userInfoStr);
                userStore.setUserInfo(userInfo);
            }

            return {
                idToken: idToken,
                accessToken: accessToken
            };
        }

        return null;
    }

    static async getConfig() {
        let accessToken = userStore.accessToken.toString();

        // Check if the id token is expired
        const expiresAt = new Date(userStore.accessToken?.payload?.exp*1000);
        const now = new Date();
        // Refresh token
        if(now >= expiresAt) {
            userStore.setRefreshingToken(true);
            await nextTick();
            const tokens = await this.getSession(true);
            userStore.setRefreshingToken(false);

            accessToken = tokens.accessToken
        }

        return {
            headers: {
                'Authorization': accessToken
            }
        }
    }

    static async get(app, path, requestParams = '', version='v1') {
        const config = await this.getConfig();
        return new Promise((resolve, reject) => {
            axios.get(`${import.meta.env.VUE_APP_API_ENDPOINT}/${app}/${path}/${version}?${requestParams}`, config)
                .then(response => {
                    resolve(response.data);
                })
                .catch(error => {
                    reject(error);
                    this.handleError(error);
                })
        });
    }

    static async getRaw(path) {
        return new Promise((resolve, reject) => {
            axios.get(path)
                .then(response => {
                    resolve(response.data);
                })
                .catch(error => {
                    reject(error);
                    this.handleError(error);
                })
        });
    }

    // Axios will attempt to read a file from a url
    static async getFile(path) {
        return new Promise((resolve, reject) => {
            axios.get(`${path}`)
                .then(response => {
                    let xml = null;
                    let json = null;

                    switch(response.headers['content-type']) {
                        case 'application/json':
                            json = JSON.stringify(response.data);
                            break;
                        case 'text/xml':
                        case 'application/xml':
                            xml = response.data;
                            break;
                    }

                    resolve({
                        json: json,
                        xml: xml
                    });
                })
                .catch(error => {
                    reject(error);
                    this.handleError(error);
                });
        });
    }

    static async post(app, path, data, requestParams = '', version = 'v1') {
        const config = await this.getConfig();
        return new Promise((resolve, reject) => {
            axios.post(`${import.meta.env.VUE_APP_API_ENDPOINT}/${app}/${path}/${version}?${requestParams}`, data, config)
                .then(response => {
                    resolve(response.data);
                })
                .catch(error => {
                    reject(error);
                    this.handleError(error);
                })
        });
    }

    static async put(path, data, version='v1') {
        const config = await this.getConfig();
        return new Promise((resolve, reject) => {
            axios.put(`${import.meta.env.VUE_APP_API_ENDPOINT}/${path}/${version}`, data, config)
                .then(response => {
                    resolve(response.data);
                })
                .catch(error => {
                    reject(error);
                    this.handleError(error);
                })
        });
    }

    static handleError(error) {
        // This is a custom error code that tells the webapp to sign the user out (Their account has been deactivated)
        if(error?.response?.status === 490) {
            this.signOut();        
        }
    }
    
    static signOut() {
        const auth = useAuthenticator();
        auth.signOut();
    }
}